import React, { useCallback, useEffect, useRef, useState } from "react";
import { TableSessionsFetchAPI } from "../../../../api/TableSessions";
import AlertSmall from "../../../Alerts/AlertSmall";
import "../../css/modals-history.css";
import "../../css/table-discounts.css";

/* ============================================================
   TableDiscountRequestsModal — el cliente e-commerce pidio un descuento sobre uno de sus productos
   (ej. jubilado) y aqui el personal lo aprueba (eligiendo uno de los descuentos de la sucursal) o lo
   rechaza. Backend: GET/POST /table-sessions/:session_uuid/discount-requests.
   Se abre desde el aviso (Swal) de la solicitud, el boton "Descuento solicitado" de la card de la mesa
   o el evento del historial.
   ============================================================ */

const money = (value) => `$${Number(value || 0).toFixed(2)}`;
const unitDiscountFor = (price, rate) => Math.round(Number(price || 0) * Number(rate || 0)) / 100;

const formatTime = (iso) => {
  const date = iso ? new Date(iso) : null;
  if (!date || Number.isNaN(date.getTime())) return "";
  return date.toLocaleTimeString("es-PA", { hour: "numeric", minute: "2-digit", hour12: true });
};

export default function TableDiscountRequestsModal({ open, onClose, table, onResolved }) {
  const sessionUuid = table?.tableSession?.session_uuid || null;
  // Una mesa con dos QR tiene dos sesiones abiertas: las solicitudes de descuento de ambas se revisan
  // aqui (cada solicitud recuerda de que sesion es, porque aprobar/rechazar se hace por sesion).
  const openSessionUuids = (Array.isArray(table?.tableSession?.open_session_uuids) && table.tableSession.open_session_uuids.length > 0)
    ? table.tableSession.open_session_uuids
    : (sessionUuid ? [sessionUuid] : []);
  const sessionUuidsKey = openSessionUuids.join("|");
  const token = localStorage.getItem("x-access-machine-token");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [requests, setRequests] = useState([]);
  const [discounts, setDiscounts] = useState([]);
  const [chosen, setChosen] = useState({}); // line_uuid -> discount_id
  const [rejecting, setRejecting] = useState({}); // line_uuid -> bool
  const [notes, setNotes] = useState({}); // line_uuid -> motivo del rechazo
  const [busyLine, setBusyLine] = useState(null);

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!sessionUuidsKey) return { requests: [], discounts: [] };
    if (!silent) setLoading(true);
    setError("");
    try {
      // En paralelo, una consulta por sesion abierta de la mesa (normalmente una; dos con dos QR).
      const responses = await Promise.all(
        sessionUuidsKey.split("|").map((uuid) => TableSessionsFetchAPI.getDiscountRequests(uuid, token).then((response) => ({ uuid, response })))
      );
      const data = {
        requests: responses.flatMap(({ uuid, response }) => (response?.data?.data?.requests || []).map((request) => ({ ...request, session_uuid: uuid }))),
        discounts: responses[0]?.response?.data?.data?.discounts || []
      };
      setRequests(data.requests || []);
      setDiscounts(data.discounts || []);

      // El modal nunca se desmonta (Reservation.jsx lo mantiene montado y solo alterna `open`), asi que
      // sin esto el estado de UI de una solicitud ya resuelta (rechazar en curso, motivo escrito, chip
      // elegido) se quedaba pegado por line_uuid - si el cliente volvia a pedir descuento sobre el MISMO
      // producto, la nueva solicitud reaparecia directo en modo "confirmar rechazo". Se descarta todo lo
      // que ya no corresponde a una solicitud pendiente actual.
      const pendingLineUuids = new Set((data.requests || []).map((request) => request.line_uuid));
      const keepPending = (prev) => Object.fromEntries(
        Object.entries(prev).filter(([lineUuid]) => pendingLineUuids.has(lineUuid))
      );
      setRejecting(keepPending);
      setNotes(keepPending);

      // Por defecto el primer descuento de la lista (el mesero puede cambiarlo antes de aprobar).
      setChosen((prev) => {
        const next = keepPending(prev);
        (data.requests || []).forEach((request) => {
          if (!next[request.line_uuid] && data.discounts?.length) next[request.line_uuid] = data.discounts[0].discount_id;
        });
        return next;
      });
      return data;
    } catch (err) {
      // Un refresco en segundo plano que falla no debe pintar un error encima de una lista valida.
      if (!silent) setError(err?.response?.data?.error || "No se pudieron cargar las solicitudes de descuento");
      return { requests: [], discounts: [] };
    } finally {
      if (!silent) setLoading(false);
    }
  }, [sessionUuidsKey, token]);

  // La lista se mantiene al dia MIENTRAS el modal esta abierto: cada evento nuevo de la mesa (el cliente
  // quito el producto -> "Solicitud de descuento cancelada", pidio otro descuento, la resolvio otro POS...)
  // vuelve a pedir las solicitudes pendientes. Antes solo se cargaba al abrir y tras aprobar/rechazar, asi
  // que una solicitud de un producto ya eliminado seguia ahi (y aprobarla daba error) aunque el historial de
  // la mesa ya la mostrara como cancelada.
  const tableEvents = Array.isArray(table?.events) ? table.events : [];
  const refreshKey = `${tableEvents.length}:${tableEvents.length ? tableEvents[tableEvents.length - 1].id : ""}`;
  const loadedOnceRef = useRef(false);
  useEffect(() => {
    if (!open) {
      loadedOnceRef.current = false;
      return;
    }
    // La primera carga muestra "Cargando..."; las siguientes son silenciosas.
    load({ silent: loadedOnceRef.current });
    loadedOnceRef.current = true;
  }, [open, load, refreshKey]);

  if (!open) return null;

  const resolve = async (request, action) => {
    if (busyLine) return;
    setBusyLine(request.line_uuid);
    try {
      await TableSessionsFetchAPI.resolveDiscountRequest(
        request.session_uuid || sessionUuid,
        request.line_uuid,
        action === "APPROVE"
          ? { action, discount_id: chosen[request.line_uuid] }
          : { action, note: (notes[request.line_uuid] || "").trim() },
        token
      );
      AlertSmall(action === "APPROVE" ? "Descuento aprobado" : "Descuento rechazado", action === "APPROVE" ? "success" : "info");
      await onResolved?.(table?.id);
      const data = await load();
      if (!data.requests.length) onClose?.();
    } catch (err) {
      AlertSmall(err?.response?.data?.error || "No se pudo resolver la solicitud", "warning");
      // Puede que ya la resolvio otro POS o el cliente quito el producto: se refresca la lista.
      load();
    } finally {
      setBusyLine(null);
    }
  };

  return (
    <div className="cds-x-ovl" onClick={onClose}>
      <div className="cds-x-mdl" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="cds-x-mdl-head">
          <div>
            <div className="cds-x-mdl-title">Solicitud de descuento </div>
            <div className="cds-x-mdl-sub">Mesa {table?.number}</div>
          </div>
          <button className="cds-x-mdl-close" type="button" onClick={onClose} aria-label="Cerrar">
            <i className="bi bi-x-lg" />
          </button>
        </div>

        <div className="cds-x-mdl-body">
          {loading && requests.length === 0 ? <div className="cds-x-mdl-empty">Cargando...</div> : null}
          {error ? <div className="cds-x-dsc-error">{error}</div> : null}
          {!loading && !error && requests.length === 0 ? (
            <div className="cds-x-mdl-empty">No hay solicitudes de descuento pendientes</div>
          ) : null}

          {requests.map((request) => {
            const selectedDiscount = discounts.find((option) => String(option.discount_id) === String(chosen[request.line_uuid]));
            const unitOff = selectedDiscount ? unitDiscountFor(request.price, selectedDiscount.rate) : 0;
            const busy = busyLine === request.line_uuid;
            const isRejecting = Boolean(rejecting[request.line_uuid]);

            return (
              <div className="cds-x-dsc-card" key={request.line_uuid}>
                <div className="cds-x-dsc-top">
                  <div>
                    <div className="cds-x-dsc-name">{request.amount}× {request.name}</div>
                    <div className="cds-x-dsc-meta">
                      {request.requested_by || "Cliente"}
                      {request.requested_at ? ` · ${formatTime(request.requested_at)}` : ""}
                    </div>
                  </div>
                  <div className="cds-x-dsc-price">{money(request.price)} c/u</div>
                </div>

                {request.reason ? <div className="cds-x-dsc-reason">Motivo: {request.reason}</div> : null}

                {discounts.length === 0 ? (
                  <div className="cds-x-dsc-error">La sucursal no tiene descuentos porcentuales configurados.</div>
                ) : (
                  <>
                    <div className="cds-x-dsc-label">Descuento a aplicar</div>
                    <div className="cds-x-dsc-chips">
                      {discounts.map((option) => (
                        <button
                          key={option.discount_id}
                          type="button"
                          className={`cds-x-dsc-chip ${String(chosen[request.line_uuid]) === String(option.discount_id) ? "active" : ""}`}
                          onClick={() => setChosen((prev) => ({ ...prev, [request.line_uuid]: option.discount_id }))}
                        >
                          {option.name} · {option.rate}%
                        </button>
                      ))}
                    </div>
                    {selectedDiscount ? (
                      <div className="cds-x-dsc-preview">
                        −{money(unitOff)} por unidad → {money(Number(request.price) - unitOff)} c/u
                        {request.amount > 1 ? ` (−${money(unitOff * request.amount)} en total)` : ""}
                      </div>
                    ) : null}
                  </>
                )}

                {isRejecting ? (
                  <input
                    className="cds-x-dsc-input"
                    type="text"
                    maxLength={120}
                    placeholder="Motivo del rechazo (opcional)"
                    value={notes[request.line_uuid] || ""}
                    onChange={(e) => setNotes((prev) => ({ ...prev, [request.line_uuid]: e.target.value }))}
                  />
                ) : null}

                <div className="cds-x-dsc-actions">
                  {isRejecting ? (
                    <>
                      <button type="button" className="cds-x-mbtn ghost" disabled={busy} onClick={() => setRejecting((prev) => ({ ...prev, [request.line_uuid]: false }))}>
                        Volver
                      </button>
                      <button type="button" className="cds-x-mbtn danger" disabled={busy} onClick={() => resolve(request, "REJECT")}>
                        {busy ? "Rechazando..." : "Confirmar rechazo"}
                      </button>
                    </>
                  ) : (
                    <>
                      <button type="button" className="cds-x-mbtn ghost" disabled={busy} onClick={() => setRejecting((prev) => ({ ...prev, [request.line_uuid]: true }))}>
                        Rechazar
                      </button>
                      <button
                        type="button"
                        className="cds-x-mbtn success"
                        disabled={busy || !selectedDiscount}
                        onClick={() => resolve(request, "APPROVE")}
                      >
                        {busy ? "Aprobando..." : "Aprobar"}
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div className="cds-x-mdl-foot">
          <button className="cds-x-mbtn ghost" type="button" onClick={onClose}>Cerrar</button>
        </div>
      </div>
    </div>
  );
}
